import { css } from '@emotion/react';
import { V1 as AiSettingsV1 } from '@frontend/api-ai-settings';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Button,
  Modal,
  ModalControlModalProps,
  TextLink,
  Text,
  TextField,
  useFormField,
  Heading,
  CopyToClipboardButton,
  EmailField,
  useAlert,
} from '@frontend/design-system';

export const OnlineBookingInstallationModal = ({
  businessId = '',
  locationId,
  orgId,
  ...modalProps
}: ModalControlModalProps & { businessId?: string; locationId: string; orgId: string }) => {
  const { t } = useTranslation('ai-receptionist-settings');

  return (
    <Modal {...modalProps} maxWidth={800}>
      <Modal.Header onClose={modalProps.onClose}>{t('Install Online Booking')}</Modal.Header>
      <Modal.Body>
        <CodeSnippet businessId={businessId} />
        <SendToYourDeveloper locationId={locationId} orgId={orgId} />
        <TextLink to='mailto:support@truelark.com' weight='bold'>
          {t('Contact Support')}
        </TextLink>
      </Modal.Body>
    </Modal>
  );
};

const CodeSnippet = ({ businessId }: { businessId: string }) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const textAreaProps = useFormField({
    value: `https://truelark.com/bookonline/#/location?businessId=${businessId}`,
    type: 'text',
  });

  return (
    <Text as='div' css={styles.container}>
      <Heading level={3}>{t('Copy URL')}</Heading>
      <Text>{t('Copy the URL below to embed the AI Receptionist as an iframe on your website.')}</Text>
      <Text>{t('Also add a button to show/hide the iframe.')}</Text>
      <div css={styles.row}>
        <TextField
          readOnly
          containerCss={css`
            flex: 1;
          `}
          {...textAreaProps}
          label=''
          name='installation-code-snippet'
        />
        <CopyToClipboardButton
          textToCopy={textAreaProps.value}
          hasSuccessTooltip
          tooltipText={t('Copy URL')}
          tooltipSuccessText={t('URL copied!')}
          size='large'
        >
          {t('Copy')}
        </CopyToClipboardButton>
      </div>
    </Text>
  );
};

const SendToYourDeveloper = ({ locationId, orgId }: { locationId: string; orgId: string }) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const alert = useAlert();
  const emailProps = useFormField({
    value: '',
    type: 'email',
  });

  const { mutate: sendEmail } = AiSettingsV1.Mutations.useSendOnlineBookingSettingsInstructionEmailMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Email sent successfully'));
        emailProps.onChange({ name: 'email', value: '' });
      },
      onError: () => {
        alert.error(t('Failed to send email'));
      },
    },
  });

  const handleSendEmail = () =>
    sendEmail({
      email: emailProps.value,
      locationId: locationId,
      orgId: orgId,
    });

  return (
    <Text as='div' css={styles.container}>
      <Heading level={3}>{t('Send to Developer')}</Heading>
      <Text>{t('We’ll email the technical instructions and link to your team.')}</Text>
      <div css={styles.row}>
        <EmailField
          containerCss={css`
            flex: 1;
          `}
          label={t('Email Address')}
          name='email-address'
          {...emailProps}
        />
        <Button
          iconName='send'
          variant='secondary'
          size='large'
          onClick={handleSendEmail}
          disabled={!emailProps.value || !!emailProps.error}
        >
          {t('Send')}
        </Button>
      </div>
    </Text>
  );
};

const styles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  }),
  row: css({
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
  }),
};
